// Phase 1A Step 34 — Section 04 ROLLOVER table (orchestrator).
// Verbatim port from handoff/landing-reference/sections.jsx RolloverTable.
// Twin headline cards (Protected vs Full) + week-by-week breakdown extracted
// to keep this file under 250 LOC.

import { buildChain } from "./calc-utils.ts";
import { RolloverBreakdown } from "./RolloverBreakdown.tsx";
import { RolloverTwinCards } from "./RolloverTwinCards.tsx";

interface RolloverTableProps {
  stake: number;
  weeks: number;
  odds: number;
  takeout: number;
  symbol: string;
}

export function RolloverTable({
  stake,
  weeks,
  odds,
  takeout,
  symbol,
}: RolloverTableProps): JSX.Element {
  const protectedRows = buildChain(stake, weeks, odds, takeout);
  const fullRows = buildChain(stake, weeks, odds, 0);
  const protectedTotal = protectedRows[protectedRows.length - 1]!.pocketIfStop;
  const fullTotal = fullRows[fullRows.length - 1]!.win;
  const totalKeptBeforeFinal = protectedRows[protectedRows.length - 2]
    ? protectedRows[protectedRows.length - 2]!.cumKept
    : 0;
  const finalWin = protectedRows[protectedRows.length - 1]!.win;
  const tkPct = Math.round(takeout * 100);

  return (
    <section
      className="sz-l-section"
      style={{
        position: "relative",
        padding: "160px 0 180px",
        borderTop: "1px solid var(--border)",
        background: "linear-gradient(180deg, #0A0A0A 0%, #0C0B08 100%)",
      }}
    >
      <div className="container">
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            marginBottom: 60,
          }}
        >
          <div>
            <span className="bracket">Section 04 · Rollover</span>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              Two paths.
              <br />
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>Honest math on both.</em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              Full rollover is the headline number. It&apos;s also the most fragile — one missed leg
              in week 9 and you walk away with nothing. The {tkPct}% takeout path is what STRIKZA
              recommends: pull half off the table every week, restake the rest. Smaller ceiling.
              Real money in your pocket from week one.
            </p>
          </div>
        </div>

        <RolloverTwinCards
          protectedTotal={protectedTotal}
          fullTotal={fullTotal}
          totalKeptBeforeFinal={totalKeptBeforeFinal}
          finalWin={finalWin}
          stake={stake}
          weeks={weeks}
          takeout={takeout}
          symbol={symbol}
        />

        <RolloverBreakdown
          rows={protectedRows}
          weeks={weeks}
          takeout={takeout}
          symbol={symbol}
          protectedTotal={protectedTotal}
          totalKeptBeforeFinal={totalKeptBeforeFinal}
          finalWin={finalWin}
        />

        <p
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--text-dim)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginTop: 24,
            textAlign: "right",
          }}
        >
          / Each week the agent shows you both paths · You choose · Single missed leg breaks the
          chain.
        </p>
      </div>
    </section>
  );
}
